import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// StratIA Logo
const StratIALogo = ({ size = 32 }) => (
  <img 
    src="/stratia-logo.png" 
    alt="StratIA" 
    className="rounded-lg"
    style={{ width: size, height: size }}
  />
);

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <StratIALogo size={32} />
            <span className="font-bold text-xl gradient-neural-text">StratIA</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:underline mb-8">
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>

        <h1 className="text-4xl font-bold mb-8">Política de Privacidad</h1>
        
        <div className="prose prose-invert max-w-none space-y-6 text-muted-foreground">
          <p className="text-lg">
            Última actualización: Febrero 2025
          </p>

          <section className="bg-card/50 p-6 rounded-lg border border-border">
            <p className="text-foreground">
              <strong>StratIA</strong> ("la Compañía", "nosotros", "nuestro") opera la plataforma de gestión de redes sociales 
              StratIA (el "Servicio"). Esta Política de Privacidad describe cómo recopilamos, usamos y protegemos su información personal.
            </p>
            <p className="mt-4">
              <strong>Nombre de la Compañía:</strong> StratIA<br />
              <strong>Sitio web:</strong> stratia.vip<br />
              <strong>Contacto:</strong> legal@stratia.vip
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">1. Información que Recopilamos</h2>
            <p>
              En StratIA recopilamos la siguiente información:
            </p>
            
            <h3 className="text-xl font-medium text-foreground mt-6 mb-3">1.1 Información de Cuenta</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Nombre y dirección de correo electrónico</li>
              <li>Contraseña (almacenada de forma encriptada)</li>
              <li>Preferencias de usuario</li>
            </ul>

            <h3 className="text-xl font-medium text-foreground mt-6 mb-3">1.2 Información de Redes Sociales</h3>
            <p>
              Cuando conecta sus cuentas de redes sociales, recopilamos:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Nombre de usuario y nombre para mostrar</li>
              <li>Foto de perfil</li>
              <li>Tokens de acceso para publicar en su nombre</li>
              <li>Métricas públicas de sus publicaciones</li>
            </ul>

            <h3 className="text-xl font-medium text-foreground mt-6 mb-3">1.3 Contenido</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Publicaciones que crea y programa</li>
              <li>Imágenes y videos que sube</li>
              <li>Hashtags y configuraciones de publicación</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">2. Cómo Usamos su Información</h2>
            <p>
              Utilizamos la información recopilada para:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Proporcionar y mantener el Servicio</li>
              <li>Publicar contenido en las plataformas sociales conectadas según sus instrucciones</li>
              <li>Enviar notificaciones sobre el estado de sus publicaciones</li>
              <li>Mejorar y personalizar su experiencia</li>
              <li>Generar análisis y estadísticas de rendimiento</li>
              <li>Comunicarnos con usted sobre actualizaciones del servicio</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">3. Compartir Información</h2>
            <p>
              <strong>No vendemos su información personal.</strong> Compartimos información únicamente en estos casos:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Plataformas Sociales:</strong> Para publicar contenido en su nombre</li>
              <li><strong>Proveedores de Servicios:</strong> Que nos ayudan a operar el servicio (hosting, análisis)</li>
              <li><strong>Cumplimiento Legal:</strong> Cuando sea requerido por ley</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">4. Seguridad de Datos</h2>
            <p>
              Implementamos medidas de seguridad para proteger su información:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Encriptación de datos en tránsito (HTTPS/TLS)</li>
              <li>Tokens de acceso encriptados en reposo</li>
              <li>Autenticación segura con contraseñas hasheadas</li>
              <li>Acceso restringido a datos personales</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">5. Sus Derechos</h2>
            <p>
              Usted tiene derecho a:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Acceder:</strong> Solicitar una copia de sus datos personales</li>
              <li><strong>Rectificar:</strong> Corregir información inexacta</li>
              <li><strong>Eliminar:</strong> Solicitar la eliminación de su cuenta y datos</li>
              <li><strong>Desconectar:</strong> Revocar acceso a sus redes sociales en cualquier momento</li>
              <li><strong>Exportar:</strong> Obtener sus datos en formato portable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">6. Retención de Datos</h2>
            <p>
              Conservamos sus datos mientras su cuenta esté activa. Al eliminar su cuenta:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Sus datos personales se eliminan en un plazo de 30 días</li>
              <li>Los tokens de redes sociales se revocan inmediatamente</li>
              <li>Podemos retener datos anónimos para análisis agregados</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">7. Cookies</h2>
            <p>
              Utilizamos cookies esenciales para:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Mantener su sesión iniciada</li>
              <li>Recordar sus preferencias</li>
              <li>Proteger contra ataques CSRF</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">8. Integraciones de Terceros</h2>
            <p>
              StratIA se integra con las siguientes plataformas, cada una con su propia política de privacidad:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Instagram / Facebook (Meta)</li>
              <li>Twitter / X</li>
              <li>TikTok</li>
              <li>YouTube (Google)</li>
              <li>LinkedIn</li>
              <li>Pinterest</li>
            </ul>
            <p className="mt-4">
              Le recomendamos revisar las políticas de privacidad de cada plataforma que conecte.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">9. Cambios a esta Política</h2>
            <p>
              Podemos actualizar esta política ocasionalmente. Le notificaremos sobre cambios significativos 
              por correo electrónico o mediante un aviso en el Servicio.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">10. Contacto</h2>
            <p>
              Para preguntas sobre esta Política de Privacidad o para ejercer sus derechos, contacte:
            </p>
            <p className="mt-2 text-primary">
              privacidad@stratia.vip
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center text-muted-foreground">
          <p>&copy; 2025 StratIA. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
